import { Dialog, Table, Badge, Pane, IconButton, Text, TickCircleIcon, CrossIcon, Card } from 'evergreen-ui';
import useWindowSize from '../../utils/useWindowSize';
import { useRole } from '../../contexts/RoleContext';

const LEAVE_TYPE_BADGE_COLORS = { Ferie: 'green', Malattia: 'red', Infortunio: 'teal', Permesso: 'yellow' };
const leaveTypeBadgeColor = (leaveType) => LEAVE_TYPE_BADGE_COLORS[leaveType] || 'yellow';

export default function TurniManagerFerieModal({
  isOpen,
  onClose,
  managerFerieList,
  onUpdateStatus,
}) {
  const { effectiveIsDirector: isDirector } = useRole();
  const width = useWindowSize();
  const isMobile = width < 768;

  const formatDate = (dateStr) => {
    return dateStr && dateStr.includes('-') ? dateStr.split('-').reverse().join('/') : dateStr;
  };

  return (
    <Dialog
      isShown={isOpen}
      title={isDirector ? "Gestione richieste ferie" : "Le mie richieste ferie"}
      onCloseComplete={onClose}
      hasFooter={false}
      width={isMobile ? '95%' : 1400}
      contentContainerProps={{ padding: isMobile ? 8 : 24 }}
    >
      {managerFerieList.length === 0 ? (
        <Text>Nessuna richiesta di ferie trovata.</Text>
      ) : isMobile ? (
        <Pane display="flex" flexDirection="column" gap={16} height={400} overflowY="auto" padding={4}>
          {managerFerieList.map(req => (
            <Card key={req.id} background="tint1" padding={20} border="default" borderRadius={8} elevation={1}>
              <Pane display="flex" justifyContent="space-between" marginBottom={12}>
                <Text fontWeight={600}>{req.operator_name}</Text>
                <Badge color={req.status === 'approvata' ? 'green' : req.status === 'rifiutata' ? 'red' : 'neutral'}>
                  {req.status === 'in_attesa' ? 'In Attesa' : req.status.toUpperCase()}
                </Badge>
              </Pane>
              <Pane marginBottom={8}>
                <Text size={300} style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
                  Dal: {formatDate(req.start_date)}{req.start_time && ['Permesso', 'Ferie'].includes(req.leave_type) ? ` ${req.start_time.substring(0,5)}` : ''} - Al: {formatDate(req.end_date)}{req.end_time && ['Permesso', 'Ferie'].includes(req.leave_type) ? ` ${req.end_time.substring(0,5)}` : ''}
                </Text>
                <Badge marginLeft={8} color={leaveTypeBadgeColor(req.leave_type)}>
                  {req.leave_type || 'Ferie'}
                </Badge>
              </Pane>
              <Pane marginBottom={isDirector || req.attachment_path ? 12 : 0}>
                <Text size={300} color="muted" style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
                  Motivo: {req.motivazione}
                </Text>
                {req.attachment_path && req.attachment_path.split(',').map((path, idx) => (
                  <Text key={idx} size={300} marginTop={8} display="block">
                    <a href={`https://www.casaletizia.net/api/${path.trim()}`} target="_blank" rel="noreferrer" style={{color: '#1070CA', textDecoration: 'none'}}>
                      📄 Visualizza allegato {req.attachment_path.split(',').length > 1 ? idx + 1 : ''}
                    </a>
                  </Text>
                ))}
              </Pane>
              {isDirector && (
                <Pane display="flex" gap={8} justifyContent="flex-end">
                  {req.status !== 'approvata' && (
                    <IconButton icon={TickCircleIcon} intent="success" title="Approva" onClick={() => onUpdateStatus(req.id, 'approvata')} />
                  )}
                  {req.status !== 'rifiutata' && (
                    <IconButton icon={CrossIcon} intent="danger" title="Rifiuta" onClick={() => onUpdateStatus(req.id, 'rifiutata')} />
                  )}
                </Pane>
              )}
            </Card>
          ))}
        </Pane>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell flexBasis={280} flexShrink={0} flexGrow={1}>Collaboratore</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={150} flexShrink={0} flexGrow={1}>Dal</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={150} flexShrink={0} flexGrow={1}>Al</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={110} flexShrink={0}>Tipo assenza</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={250} flexShrink={0} flexGrow={2}>Motivo</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={120} flexShrink={0}>Stato</Table.TextHeaderCell>
            {isDirector && <Table.TextHeaderCell flexBasis={120} flexShrink={0}>Azioni</Table.TextHeaderCell>}
          </Table.Head>
          <Table.Body height={400}>
            {managerFerieList.map(req => (
              <Table.Row key={req.id} height="auto" minHeight={48} paddingY={8}>
                <Table.Cell flexBasis={280} flexShrink={0} flexGrow={1} display="flex" alignItems="center">
                  <Text whiteSpace="normal" wordBreak="break-word">
                    {req.operator_name}
                  </Text>
                </Table.Cell>
                <Table.Cell display="flex" flexWrap="wrap" alignItems="center" flexBasis={150} flexShrink={0} flexGrow={1}>
                  <Text style={{ whiteSpace: 'normal', wordBreak: 'break-word', marginRight: 8 }}>
                    {formatDate(req.start_date)}{req.start_time && ['Permesso', 'Ferie'].includes(req.leave_type) ? ` ${req.start_time.substring(0,5)}` : ''}
                  </Text>
                </Table.Cell>
                <Table.Cell display="flex" alignItems="center" flexBasis={150} flexShrink={0} flexGrow={1}>
                  <Text style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {formatDate(req.end_date)}{req.end_time && ['Permesso', 'Ferie'].includes(req.leave_type) ? ` ${req.end_time.substring(0,5)}` : ''}
                  </Text>
                </Table.Cell>
                <Table.Cell display="flex" alignItems="center" flexBasis={110} flexShrink={0}>
                  <Badge color={leaveTypeBadgeColor(req.leave_type)}>
                    {req.leave_type || 'Ferie'}
                  </Badge>
                </Table.Cell>
                <Table.Cell flexBasis={250} flexShrink={0} flexGrow={2}>
                  <Text style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>{req.motivazione}</Text>
                  {req.attachment_path && req.attachment_path.split(',').map((path, idx) => (
                    <Text key={idx} size={300} marginTop={4} display="block">
                      <a href={`https://www.casaletizia.net/api/${path.trim()}`} target="_blank" rel="noreferrer" style={{color: '#1070CA', textDecoration: 'none'}}>
                        📄 Allegato {req.attachment_path.split(',').length > 1 ? idx + 1 : ''}
                      </a>
                    </Text>
                  ))}
                </Table.Cell>
                <Table.Cell flexBasis={120} flexShrink={0} display="flex" alignItems="center">
                   <Badge color={req.status === 'approvata' ? 'green' : req.status === 'rifiutata' ? 'red' : 'neutral'}>
                     {req.status === 'in_attesa' ? 'In Attesa' : req.status.toUpperCase()}
                   </Badge>
                </Table.Cell>
                {isDirector && (
                  <Table.Cell flexBasis={120} flexShrink={0}>
                    <Pane display="flex" gap={8}>
                      {req.status !== 'approvata' && (
                        <IconButton icon={TickCircleIcon} intent="success" title="Approva" onClick={() => onUpdateStatus(req.id, 'approvata')} />
                      )}
                      {req.status !== 'rifiutata' && (
                        <IconButton icon={CrossIcon} intent="danger" title="Rifiuta" onClick={() => onUpdateStatus(req.id, 'rifiutata')} />
                      )}
                    </Pane>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Dialog>
  );
}